import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import {clsx} from 'clsx'
import {RTE as RichTextComponent} from '@/products/flexible-web-toolkit/components/rte'
import {spacing} from '@/products/flexible-web-toolkit/styles'
import type {Block, Tersed} from '@/services/kontent-ai/types'
import {RichTextRenderer} from '@/products/flexible-web-toolkit/components/rich-text-renderer'
import {colorWithFallback} from '@/utilities/hooks/color-with-fallback'
import {getColor} from '@/products/flexible-web-toolkit/colors'
import type {CtaButtonContentItem} from '../cta-button'
import {ReusableCTAButtonComponent} from '../cta-button'
import {MikMakButtonBlock} from '@/products/where-to-buy/mikmak/mikmak-button'
import type {MikmakButtonContentItem} from '@/products/where-to-buy/mikmak/mikmak-button'

export type RichTextWithCTAContentItem = IContentItem<{
	content: Elements.RichTextElement
	ctaButton: Elements.LinkedItemsElement<
		CtaButtonContentItem | MikmakButtonContentItem
	>
	tabTitle: Elements.TextElement
	snippetSpacingSpacing: Elements.MultipleChoiceElement
	textAlignment: Elements.MultipleChoiceElement
	tableAlignment: Elements.MultipleChoiceElement
	brandColor: Elements.CustomElement
}>

export const RichTextWithCtaBlock: Block<RichTextWithCTAContentItem> = ({
	block,
	page,
	...context
}) => {
	const componentSpacing =
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- not unnecessary, snippetSpacingSpacing may be null
		spacing[
			block.elements.snippetSpacingSpacing[0]
				?.codename as keyof typeof spacing
		] || spacing.none

	// Color priority: block brandColor → page config brand color → white background
	const {colorId: color, bgClass} = colorWithFallback({
		blockColor: block.elements.brandColor,
		pageConfig: page.elements.config[0],
	})
	const buttonColor = 'bg-grey'

	// Use bgClass (safe, registry-guarded) instead of manually building bg-${color}.
	// bgClass is null for legacy-only colors not in the Tailwind safelist (e.g. 'beige'),
	// preventing invalid class generation. Fall back to white when no color is resolved.
	const backgroundColor = bgClass ?? 'bg-white'
	// Only apply color-based text contrast when we have a valid background class from
	// the registry. When bgClass is null the background is white, so always use dark text
	// to avoid white-on-white.
	const hasValidBg = Boolean(bgClass)
	const textContrast =
		hasValidBg && color
			? (getColor(color)?.textContrast ?? 'light')
			: 'dark'
	const textClass =
		textContrast === 'light' ? 'text-white' : 'text-black fill-black'
	return (
		<div className={clsx(backgroundColor, 'py-12', componentSpacing)}>
			<RichTextRenderer
				className={clsx(
					hasValidBg && color
						? `${textClass} brandtext-${color}`
						: '',
					'container-narrow px-8',
					'text-center',
					'content'
				)}
				data-kontent-element-codename="content"
				element={block.elements.content}
				page={page}
				{...context}
			/>
			<RichTextComponent className="text-center">
				<div className="justify-center sm:flex md:flex-row">
					{block.elements.ctaButton.map((button, index, arr) =>
						// Each button always resolves its own colour chain:
						// brandColor (picker) → variant (deprecated dropdown) → page config → 'primary'
						// No parent override is applied — the block background and button
						// colour are independent editor choices.
						button.system.type === 'cta_button' ? (
							<ReusableCTAButtonComponent
								block={
									button as unknown as Tersed<CtaButtonContentItem>
								}
								className={clsx(
									buttonColor,
									'mt-6 w-full max-w-xs',
									arr.length > 1 && index !== arr.length - 1
										? 'sm:mr-6'
										: ''
								)}
								ctaButtonOrigin={block.system.type} // used for analytics to identify which block the click came from
								key={button.system.id}
								page={page}
								variant={
									// @todo - this is done only for legacy support
									color
										? 'none'
										: (
												button as unknown as Tersed<CtaButtonContentItem>
											).elements.variant[0]?.codename ||
											'primary'
								}
								{...context}
							/>
						) : (
							<div
								className={clsx(
									buttonColor,
									'mx-auto mt-6 w-full max-w-xs sm:mx-0',
									arr.length > 1 && index !== arr.length - 1
										? 'sm:mr-6'
										: '',
									// MikMakButtonBlock wraps its button in a div with
									// `my-6 md:my-8` spacing and adds `mt-2` on the button
									// itself, meant for standalone use. Zero those out here
									// so the spacing matches the CTA button's, without
									// modifying the MikMakButtonBlock component itself.
									'[&>div]:my-0 [&_.swn-tag-wtb-btn]:mt-0'
								)}
								data-kontent-item-id={button.system.id}
								key={button.system.id}
							>
								<MikMakButtonBlock
									block={
										button as unknown as Tersed<MikmakButtonContentItem>
									}
									page={page}
									{...context}
								/>
							</div>
						)
					)}
				</div>
			</RichTextComponent>
		</div>
	)
}
